import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  MapPin, Clock, User, Calendar, Route, Zap, 
  ArrowRight, Users, Building2, Phone, Navigation 
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const DistributionRdvPage = () => {
  const [rdvNonAttribues, setRdvNonAttribues] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [selectedRdv, setSelectedRdv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedCommercial, setSelectedCommercial] = useState('');
  const [optimizedRoutes, setOptimizedRoutes] = useState({});
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [conversionStats, setConversionStats] = useState({
    total_prospects: 0,
    rdv_signes: 0,
    conversions: 0,
    taux_conversion: 0
  });
  const [rdvAttribues, setRdvAttribues] = useState([]);
  const [rdvSignes, setRdvSignes] = useState([]);
  const [activeTab, setActiveTab] = useState('non-attribues'); // 'non-attribues', 'attribues', 'signes'
  
  // États pour modifier le statut des RDV
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [editingRdvStatus, setEditingRdvStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newResultat, setNewResultat] = useState('');

  // États pour la carte et l'optimisation
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]); // Paris par défaut
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateRdvStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rendez-vous/${editingRdvStatus.id}`, {
        status: newStatus,
        resultat: newResultat
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Statut du RDV modifié avec succès');
      setShowStatusDialog(false);
      setEditingRdvStatus(null);
      setNewStatus('');
      setNewResultat('');
      fetchData(); // Recharger les données
    } catch (error) {
      console.error('Error updating RDV status:', error);
      toast.error('Erreur lors de la modification du statut');
    }
  };

  const openStatusDialog = (rdv) => {
    setEditingRdvStatus(rdv);
    setNewStatus(rdv.status);
    setNewResultat(rdv.resultat || '');
    setShowStatusDialog(true);
  };

  const fetchConversionStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer tous les prospects pour les stats
      const prospectsResponse = await axios.get(`${API}/api/prospects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Récupérer tous les RDV pour calculer les conversions
      const rdvResponse = await axios.get(`${API}/api/rendez-vous`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const prospects = prospectsResponse.data;
      const rdvs = rdvResponse.data;
      
      const rdvSignes = rdvs.filter(rdv => rdv.resultat === 'signe').length;
      const conversions = prospects.filter(p => p.status === 'converti_client').length;
      const tauxConversion = prospects.length > 0 ? (conversions / prospects.length * 100) : 0;
      
      setConversionStats({
        total_prospects: prospects.length,
        rdv_signes: rdvSignes,
        conversions: conversions,
        taux_conversion: tauxConversion
      });
    } catch (error) {
      console.error('Error fetching conversion stats:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer la liste des commerciaux/régies d'abord
      const usersResponse = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrer pour ne garder que les commerciaux et régies
      const commerciauxData = usersResponse.data.filter(
        user => user.role === 'commercial' || user.role === 'regie'
      );
      setCommerciaux(commerciauxData);

      // Récupérer tous les prospects une seule fois
      const prospectsResponse = await axios.get(`${API}/api/prospects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Récupérer les RDV non attribués
      const rdvNonAttribuesResponse = await axios.get(`${API}/api/rendez-vous/non-attribues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRdvNonAttribues(rdvNonAttribuesResponse.data);

      // Récupérer tous les RDV pour voir ceux qui sont attribués
      const allRdvResponse = await axios.get(`${API}/api/rendez-vous`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrer pour ne garder que les RDV attribués
      const rdvAttribuesFiltered = allRdvResponse.data.filter(rdv => 
        rdv.assigned_to && rdv.assigned_to !== "" && rdv.assigned_to !== null
      );
      
      // Enrichir avec les données du prospect et du commercial
      const rdvEnriched = rdvAttribuesFiltered.map(rdv => {
        // Trouver le prospect correspondant
        const prospect = prospectsResponse.data.find(p => p.id === rdv.prospect_id);
        
        // Trouver le commercial correspondant
        const commercial = commerciauxData.find(c => c.id === rdv.assigned_to);
        
        return {
          ...rdv,
          prospect: prospect || null,
          commercial: commercial || null
        };
      });
      setRdvAttribues(rdvEnriched);

      // Filtrer et enrichir les RDV signés (par date décroissante)
      const rdvSignesFiltered = allRdvResponse.data
        .filter(rdv => rdv.resultat === 'signe')
        .sort((a, b) => new Date(b.date_rdv) - new Date(a.date_rdv)); // Du plus récent au plus ancien
      
      const rdvSignesEnriched = rdvSignesFiltered.map(rdv => {
        const prospect = prospectsResponse.data.find(p => p.id === rdv.prospect_id);
        const commercial = commerciauxData.find(c => c.id === rdv.assigned_to);
        
        return {
          ...rdv,
          prospect: prospect || null,
          commercial: commercial || null
        };
      });
      
      setRdvSignes(rdvSignesEnriched);

      // Récupérer les statistiques de conversion
      await fetchConversionStats();
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRdv = (rdv) => {
    setSelectedRdv(prev => {
      const isSelected = prev.some(r => r.id === rdv.id);
      if (isSelected) {
        return prev.filter(r => r.id !== rdv.id);
      } else {
        return [...prev, rdv];
      }
    });
  };

  const handleSelectAllRdv = () => {
    if (selectedRdv.length === rdvNonAttribues.length) {
      setSelectedRdv([]);
    } else {
      setSelectedRdv([...rdvNonAttribues]);
    }
  };

  const openAssignDialog = () => {
    if (selectedRdv.length === 0) {
      toast.error('Sélectionnez au moins un rendez-vous');
      return;
    }
    setShowAssignDialog(true);
  };

  const handleAssignRdv = async () => {
    if (!selectedCommercial) {
      toast.error('Sélectionnez un commercial/régie');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Assigner tous les RDV sélectionnés
      await Promise.all(
        selectedRdv.map(rdv => 
          axios.put(`${API}/api/rendez-vous/${rdv.id}`, {
            assigned_to: selectedCommercial,
            status: 'planifie'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );

      toast.success(`${selectedRdv.length} rendez-vous assigné(s) avec succès`);
      setShowAssignDialog(false);
      setSelectedRdv([]);
      setSelectedCommercial('');
      fetchData(); // Rafraîchir les données
      
      // Optimiser automatiquement après attribution
      if (selectedCommercial) {
        await optimizeRouteForCommercial(selectedCommercial);
      }
      
    } catch (error) {
      console.error('Error assigning RDV:', error);
      toast.error('Erreur lors de l\'attribution');
    }
  };

  // Fonction d'optimisation intelligente avec proposition d'horaires
  const optimizeRouteForCommercial = async (commercialId) => {
    setIsOptimizing(true);
    let rdvFiltered = []; // Définir en dehors du try pour être accessible dans catch
    
    try {
      // 🆕 RÉCUPÉRER L'ADRESSE DU COMMERCIAL EN PREMIER (avant filtrage RDV)
      let commercialHomeCoords = null;
      let commercialData = null;
      
      try {
        const token = localStorage.getItem('token');
        // Forcer le rechargement en ajoutant timestamp pour éviter le cache
        const commercialResponse = await axios.get(`${API}/api/users/${commercialId}?t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        commercialData = commercialResponse.data;
        console.log('[DEBUG] Données commercial récupérées:', {
          id: commercialData.id,
          nom: `${commercialData.first_name} ${commercialData.last_name}`,
          adresse: commercialData.adresse_personnelle,
          cp: commercialData.code_postal_personnel,
          ville: commercialData.ville_personnelle
        });
      } catch (error) {
        console.error('Erreur récupération commercial:', error);
      }

      const token = localStorage.getItem('token');
      const rdvCommercial = await axios.get(`${API}/api/rendez-vous`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filtrer les RDV pour le commercial et la date sélectionnée
      // Exclure les RDV avec statut "réalisé" de la planification optimisée
      rdvFiltered = rdvCommercial.data.filter(rdv => 
        rdv.assigned_to === commercialId &&
        new Date(rdv.date_rdv).toDateString() === new Date(selectedDate).toDateString() &&
        rdv.status !== "realise" // Exclure les RDV réalisés
      );

      if (rdvFiltered.length < 2) {
        toast('Moins de 2 RDV pour optimiser l\'itinéraire', { icon: 'ℹ️' });
        return;
      }

      // Préparer les coordonnées pour OpenRouteService
      const coordinates = [];
      const rdvWithCoords = [];

      // 🆕 AJOUTER L'ADRESSE DU COMMERCIAL comme point de départ SI définie
      if (commercialData?.adresse_personnelle && commercialData?.code_postal_personnel && commercialData?.ville_personnelle) {
        const fullAddress = `${commercialData.adresse_personnelle}, ${commercialData.code_postal_personnel} ${commercialData.ville_personnelle}`;
        
        console.log('[DEBUG] Géocodage adresse commercial:', fullAddress);
        
        try {
          // Géocoder l'adresse du commercial
          const geocodeResponse = await axios.get(
            `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}&limit=1`
          );
          
          if (geocodeResponse.data.features && geocodeResponse.data.features.length > 0) {
            const coords = geocodeResponse.data.features[0].geometry.coordinates;
            commercialHomeCoords = coords; // [longitude, latitude]
            coordinates.push(coords); // Ajouter comme premier point
            rdvWithCoords.push({
              id: 'home_commercial',
              is_home: true,
              adresse: fullAddress,
              nom: `🏠 Départ: ${commercialData.first_name} ${commercialData.last_name}`,
              prospect: { latitude: coords[1], longitude: coords[0] }
            });
            
            console.log('[DEBUG] Point de départ ajouté:', coords);
            toast.success(`✅ Point de départ: Domicile du commercial (${commercialData.ville_personnelle})`);
          } else {
            console.warn('[DEBUG] Géocodage échoué, pas de résultats');
            toast.warning('Adresse du commercial non trouvée, départ depuis le 1er RDV');
          }
        } catch (geoError) {
          console.error('[DEBUG] Erreur géocodage:', geoError);
          toast.warning('Erreur géocodage adresse, départ depuis le 1er RDV');
        }
      } else {
        console.log('[DEBUG] Pas d\'adresse personnelle définie pour le commercial');
        toast.info('💡 Astuce: Définissez l\'adresse du commercial dans Paramètres > Utilisateurs');
      }

      // 🆕 GÉOCODER LES RDV SANS COORDONNÉES À LA VOLÉE
      for (const rdv of rdvFiltered) {
        let lat = rdv.prospect?.latitude;
        let lon = rdv.prospect?.longitude;
        
        // Si pas de coordonnées mais adresse disponible, géocoder
        if ((!lat || !lon) && rdv.prospect?.adresse && rdv.prospect?.code_postal && rdv.prospect?.ville) {
          const fullAddress = `${rdv.prospect.adresse}, ${rdv.prospect.code_postal} ${rdv.prospect.ville}`;
          console.log(`[DEBUG] Géocodage prospect ${rdv.prospect.nom}:`, fullAddress);
          
          try {
            // 🔧 NE PAS ENVOYER LE TOKEN D'AUTH AUX APIs EXTERNES !
            const geocodeResponse = await fetch(
              `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}&limit=1`
            );
            const geoData = await geocodeResponse.json();
            
            if (geoData.features && geoData.features.length > 0) {
              const coords = geoData.features[0].geometry.coordinates;
              lat = coords[1];
              lon = coords[0];
              
              // Mettre à jour le RDV avec les coordonnées
              rdv.prospect.latitude = lat;
              rdv.prospect.longitude = lon;
              
              console.log(`[DEBUG] ✅ Géocodage réussi: ${lat}, ${lon}`);
            } else {
              console.warn(`[DEBUG] ⚠️ Géocodage échoué pour ${rdv.prospect.nom}`);
            }
          } catch (error) {
            console.error(`[DEBUG] ❌ Erreur géocodage ${rdv.prospect.nom}:`, error);
          }
        }
        
        // Ajouter uniquement si on a des coordonnées (originales ou géocodées)
        if (lat && lon) {
          coordinates.push([lon, lat]);
          rdvWithCoords.push(rdv);
        } else {
          console.warn(`[DEBUG] ⚠️ RDV ignoré (pas de coords): ${rdv.prospect?.nom || 'Inconnu'}`);
        }
      }

      if (coordinates.length < 2) {
        toast.error('Pas assez d\'adresses géolocalisées pour l\'optimisation');
        return;
      }

      try {
        // Appel au proxy backend pour éviter les erreurs CORS
        const matrixResponse = await axios.post(
          `${API}/api/routing/matrix`,
          {
            locations: coordinates,
            metrics: ["distance", "duration"],
            units: "km"
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        // Algorithme d'optimisation simple (plus proche voisin)
        const isFallbackMatrix = matrixResponse.data.fallback === true;
        // ORS retourne en mètres, fallback en km — normaliser tout en km
        const rawDistances = matrixResponse.data.distances;
        const distances = isFallbackMatrix ? rawDistances : rawDistances.map(row => row.map(d => d / 1000));
        const durations = matrixResponse.data.durations;
        
        let optimizedOrder = [];
        let unvisited = [...rdvWithCoords.map((_, i) => i)];
        let current = 0; // Commencer par le premier point
        
        optimizedOrder.push(rdvWithCoords[current]);
        unvisited.splice(unvisited.indexOf(current), 1);
        
        let totalDistance = 0;
        let totalTime = 0;
        
        while (unvisited.length > 0) {
          let nearest = unvisited[0];
          let nearestDistance = distances[current][nearest];
          
          // Trouver le point le plus proche
          for (let i = 1; i < unvisited.length; i++) {
            const candidate = unvisited[i];
            if (distances[current][candidate] < nearestDistance) {
              nearest = candidate;
              nearestDistance = distances[current][candidate];
            }
          }
          
          totalDistance += nearestDistance;
          totalTime += durations[current][nearest];
          
          optimizedOrder.push(rdvWithCoords[nearest]);
          unvisited.splice(unvisited.indexOf(nearest), 1);
          current = nearest;
        }

        // Générer des propositions d'horaires optimisées
        const proposedSchedule = [];
        let currentTime = new Date(selectedDate + ' 09:00'); // Commencer à 9h
        
        for (let i = 0; i < optimizedOrder.length; i++) {
          const rdv = optimizedOrder[i];
          
          // Ne pas ajouter le point "home" dans le schedule final (c'est juste un point de départ)
          if (rdv.is_home) continue;
          
          const originalTime = rdv.date_rdv ? new Date(rdv.date_rdv) : currentTime;
          
          // Trouver l'index de ce RDV dans le tableau complet (incluant home)
          const currentIndex = optimizedOrder.findIndex(r => r.id === rdv.id);
          const previousIndex = currentIndex > 0 ? currentIndex - 1 : 0;
          
          // Calculer travel_time et distance_from_previous depuis le point précédent (qui peut être "home")
          const travelTime = currentIndex > 0 ? Math.round(durations[previousIndex][currentIndex]) : 0;
          const distanceFromPrevious = currentIndex > 0 ? distances[previousIndex][currentIndex].toFixed(1) : 0;
          
          proposedSchedule.push({
            ...rdv,
            original_time: originalTime,
            proposed_time: new Date(currentTime),
            travel_time: travelTime,
            time_changed: Math.abs(currentTime - originalTime) > 30 * 60 * 1000, // Plus de 30min de différence
            distance_from_previous: distanceFromPrevious
          });
          
          // Ajouter la durée du RDV + temps de trajet jusqu'au suivant
          const dureeRdvMinutes = rdv.duree_rdv_minutes || 60;
          currentTime.setMinutes(currentTime.getMinutes() + dureeRdvMinutes);
          
          // Ajouter le temps de trajet vers le prochain RDV (si il y en a un)
          const nextIndex = currentIndex + 1;
          if (nextIndex < optimizedOrder.length) {
            const travelToNext = durations[currentIndex][nextIndex];
            currentTime.setMinutes(currentTime.getMinutes() + Math.ceil(travelToNext));
          }
        }

        // Vérifier la limite de 350km/jour
        const MAX_DAILY_DISTANCE = 350;
        let isOverLimit = totalDistance > MAX_DAILY_DISTANCE;
        let filteredSchedule = proposedSchedule;
        let adjustedDistance = totalDistance;
        let adjustedTime = totalTime;

        if (isOverLimit) {
          // Réduire le nombre de RDV pour respecter la limite
          let reducedSchedule = [];
          let runningDistance = 0;
          
          for (let i = 0; i < proposedSchedule.length; i++) {
            const rdv = proposedSchedule[i];
            const additionalDistance = i > 0 ? parseFloat(rdv.distance_from_previous) : 0;
            
            if (runningDistance + additionalDistance <= MAX_DAILY_DISTANCE) {
              runningDistance += additionalDistance;
              reducedSchedule.push(rdv);
            } else {
              break; // Arrêter si on dépasse la limite
            }
          }
          
          filteredSchedule = reducedSchedule;
          adjustedDistance = runningDistance;
          // Recalculer le temps pour les RDV retenus
          adjustedTime = filteredSchedule.reduce((total, rdv) => total + (rdv.travel_time || 0), 0);
        }

        const routeResult = {
          rdv: optimizedOrder,
          proposedSchedule: filteredSchedule,
          distance: adjustedDistance,
          time: Math.round(adjustedTime),
          date: selectedDate,
          isOverLimit: isOverLimit,
          originalDistance: totalDistance,
          excludedRdv: isOverLimit ? proposedSchedule.length - filteredSchedule.length : 0,
          savings: {
            time: Math.round((rdvWithCoords.length * 45) - adjustedTime),
            distance: Math.round(adjustedDistance * 0.8)
          }
        };

        setOptimizedRoutes(prev => ({
          ...prev,
          [commercialId]: routeResult
        }));

        const changedAppointments = filteredSchedule.filter(p => p.time_changed).length;
        
        if (isOverLimit) {
          toast.warning(
            `⚠️ Limite 350km dépassée! ${routeResult.excludedRdv} RDV exclus. ` +
            `Itinéraire ajusté: ${adjustedDistance.toFixed(1)}km/${MAX_DAILY_DISTANCE}km` +
            (changedAppointments > 0 ? ` | ${changedAppointments} horaires à ajuster` : '')
          );
        } else {
          toast.success(
            `🚀 Itinéraire optimisé: ${adjustedDistance.toFixed(1)}km, ${Math.round(adjustedTime)}min de route` +
            (changedAppointments > 0 ? ` | ${changedAppointments} horaires à ajuster` : ' | Horaires déjà optimaux')
          );
        }

        return routeResult;

      } catch (apiError) {
        console.error('OpenRouteService API error:', apiError);
        throw apiError;
      }

    } catch (error) {
      console.error('Error optimizing route:', error);
      
      // Fallback intelligent: ordre par heure avec analyse des distances
      const fallbackOrder = rdvFiltered.sort((a, b) => 
        new Date(a.date_rdv) - new Date(b.date_rdv)
      );
      
      const fallbackResult = {
        rdv: fallbackOrder,
        distance: 0,
        time: 0,
        date: selectedDate,
        fallback: true,
        fallbackReason: 'API temporairement indisponible'
      };

      setOptimizedRoutes(prev => ({
        ...prev,
        [commercialId]: fallbackResult
      }));
      
      toast.warning('⚠️ API d\'optimisation temporairement indisponible. Ordre chronologique appliqué.');
      return fallbackResult;
    } finally {
      setIsOptimizing(false);
    }
  };

  // Fonction pour afficher la vue optimisée jour par jour
  const renderOptimizedDailyView = () => {
    // Grouper les RDV par date et commercial
    // Exclure les RDV avec statut "réalisé"
    const rdvByDateAndCommercial = {};
    
    rdvAttribues
      .filter(rdv => rdv.status !== "realise") // Exclure les RDV réalisés de la planification
      .forEach(rdv => {
        const date = new Date(rdv.date_rdv).toISOString().split('T')[0];
        const commercialId = rdv.assigned_to;
        
        if (!rdvByDateAndCommercial[date]) {
          rdvByDateAndCommercial[date] = {};
        }
        
        if (!rdvByDateAndCommercial[date][commercialId]) {
          rdvByDateAndCommercial[date][commercialId] = [];
        }
        
        rdvByDateAndCommercial[date][commercialId].push(rdv);
      });

    // Trier les dates
    const sortedDates = Object.keys(rdvByDateAndCommercial).sort();

    return sortedDates.map(date => (
      <div key={date} className="border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold">
              {new Date(date + 'T12:00:00').toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
          </div>
          <Button
            size="sm"
            onClick={() => optimizeDateForAllCommercials(date)}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={isOptimizing}
          >
            <Zap className="w-4 h-4 mr-1" />
            Optimiser cette journée
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(rdvByDateAndCommercial[date]).map(([commercialId, rdvs]) => {
            const commercial = commerciaux.find(c => c.id === commercialId);
            
            // 🆕 UTILISER LA ROUTE OPTIMISÉE si disponible, sinon afficher les RDV triés
            const hasOptimizedRoute = optimizedRoutes[commercialId]?.proposedSchedule;
            const displayRdvs = hasOptimizedRoute 
              ? optimizedRoutes[commercialId].proposedSchedule
              : rdvs.sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
            
            return (
              <div key={commercialId} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-sm">
                    <User className="w-4 h-4 inline mr-1 text-blue-600" />
                    {commercial?.first_name} {commercial?.last_name}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {rdvs.length} RDV
                    {hasOptimizedRoute && ' ✓'}
                  </Badge>
                </div>

                <div className="space-y-2">
                  {displayRdvs.map((rdv, index) => {
                    const travelTimeToNext = calculateTravelTimeBetweenRdv(rdv, displayRdvs[index + 1], commercial);
                    
                    return (
                      <div key={rdv.id}>
                        <div className="flex items-start gap-2 text-xs">
                          <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3 text-gray-500" />
                              <span className="font-medium">
                                {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              <Badge className={getStatusColor(rdv.status)} size="sm">
                                {rdv.status}
                              </Badge>
                            </div>
                            
                            <div className="font-medium text-gray-800">
                              {rdv.prospect?.prenom} {rdv.prospect?.nom}
                              {rdv.prospect?.nom_entreprise && (
                                <span className="text-gray-600 ml-1">
                                  ({rdv.prospect.nom_entreprise})
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-start gap-1">
                              <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                              <span className="text-gray-600 text-xs leading-tight">
                                {rdv.prospect?.adresse}
                                {(rdv.prospect?.code_postal || rdv.prospect?.ville) && (
                                  <span className="block text-gray-500 mt-1">
                                    {rdv.prospect?.code_postal && `${rdv.prospect.code_postal}`}
                                    {rdv.prospect?.ville && ` ${rdv.prospect.ville}`}
                                  </span>
                                )}
                              </span>
                            </div>

                            {rdv.prospect?.telephone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-600">
                                  {rdv.prospect.telephone}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Affichage du temps de route vers le prochain RDV */}
                        {index < displayRdvs.length - 1 && (
                          <div className="ml-8 mt-2 mb-2">
                            <div className="flex items-center gap-2 text-xs text-orange-600 bg-orange-50 rounded px-2 py-1">
                              <Navigation className="w-3 h-3" />
                              <span>
                                ↓ Trajet vers {displayRdvs[index + 1].prospect?.prenom}: ~{travelTimeToNext.time}min ({travelTimeToNext.distance}km)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Résumé de la journée pour ce commercial */}
                <div className="mt-3 pt-2 border-t border-gray-200">
                  <div className="text-xs text-gray-600">
                    <div>🕐 Durée totale: {calculateTotalDayTime(displayRdvs)}</div>
                    <div>🛣️ Distance estimée: {calculateTotalDayDistance(displayRdvs, commercial)}km</div>
                    {!hasOptimizedRoute && (
                      <div className="text-orange-600 mt-1">
                        💡 Cliquez sur &quot;Optimiser&quot; pour des calculs précis
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ));
  };

  // Fonction pour calculer le temps de trajet entre deux RDV
  const calculateTravelTimeBetweenRdv = (rdv1, rdv2, commercial = null) => {
    if (!rdv2 || !rdv1.prospect?.latitude || !rdv2.prospect?.latitude) {
      console.log('[DEBUG] Calcul impossible - coordonnées manquantes:', {
        rdv1_id: rdv1?.id,
        rdv2_id: rdv2?.id,
        rdv1_coords: rdv1?.prospect ? `${rdv1.prospect.latitude}, ${rdv1.prospect.longitude}` : 'manquant',
        rdv2_coords: rdv2?.prospect ? `${rdv2.prospect.latitude}, ${rdv2.prospect.longitude}` : 'manquant'
      });
      return { time: '?', distance: '?' };
    }
    
    // 🆕 UTILISER LES DISTANCES RÉELLES si disponibles (depuis OpenRouteService via optimisation)
    if (rdv2.distance_from_previous && rdv2.travel_time) {
      console.log(`[DEBUG] Distance RÉELLE utilisée entre ${rdv1.id} et ${rdv2.id}: ${rdv2.distance_from_previous}km, ${rdv2.travel_time}min`);
      return {
        time: rdv2.travel_time,
        distance: typeof rdv2.distance_from_previous === 'string' ? rdv2.distance_from_previous : rdv2.distance_from_previous.toFixed(1)
      };
    }
    
    // Sinon, calcul approximatif basé sur la distance à vol d'oiseau (FALLBACK)
    const lat1 = rdv1.prospect.latitude;
    const lon1 = rdv1.prospect.longitude;
    const lat2 = rdv2.prospect.latitude;
    const lon2 = rdv2.prospect.longitude;
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    // Estimation du temps (vitesse moyenne 40km/h en ville, +30% pour route réelle)
    const realDistance = distance * 1.3; // Facteur correctif pour route réelle
    const timeInMinutes = Math.round(realDistance / 40 * 60);
    
    console.log(`[DEBUG] Distance FALLBACK calculée entre ${rdv1.id} et ${rdv2.id}: Vol d'oiseau=${distance.toFixed(1)}km, Réelle estimée=${realDistance.toFixed(1)}km, Temps=${timeInMinutes}min`);
    
    return {
      time: timeInMinutes,
      distance: realDistance.toFixed(1)
    };
  };

  // Fonction pour calculer le temps total d'une journée
  const calculateTotalDayTime = (rdvs) => {
    if (rdvs.length === 0) return '0h';
    
    const sortedRdvs = rdvs.sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
    const firstRdv = new Date(sortedRdvs[0].date_rdv);
    const lastRdv = new Date(sortedRdvs[sortedRdvs.length - 1].date_rdv);
    
    // Ajouter 1h pour le dernier RDV
    lastRdv.setHours(lastRdv.getHours() + 1);
    
    const diffMs = lastRdv - firstRdv;
    const diffHours = Math.round(diffMs / (1000 * 60 * 60) * 10) / 10;
    
    return `${diffHours}h`;
  };

  // Fonction pour calculer la distance totale d'une journée
  const calculateTotalDayDistance = (rdvs, commercial = null) => {
    if (rdvs.length === 0) return '0';
    
    let totalDistance = 0;
    
    // 🆕 UTILISER LES DISTANCES RÉELLES si disponibles (depuis l'optimisation)
    for (let i = 0; i < rdvs.length; i++) {
      const rdv = rdvs[i];
      if (rdv.distance_from_previous && rdv.distance_from_previous > 0) {
        totalDistance += parseFloat(rdv.distance_from_previous);
      }
    }
    
    // Si aucune distance trouvée (RDV non optimisés), fallback sur calcul approximatif
    if (totalDistance === 0 && rdvs.length >= 1) {
      console.log('[DEBUG] Pas de distances réelles, calcul fallback avec adresse commercial');
      const sortedRdvs = rdvs.sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));
      
      // 🆕 Si le commercial a une adresse, calculer distance depuis domicile vers premier RDV
      if (commercial?.adresse_personnelle && commercial?.code_postal_personnel && commercial?.ville_personnelle && sortedRdvs.length > 0) {
        // Utiliser un calcul approximatif basé sur les codes postaux
        // (approximation simplifiée en attendant géocodage)
        // TODO: Implémenter géocodage de l'adresse commercial
        totalDistance += 15; // Distance moyenne estimée domicile -> 1er RDV
        console.log('[DEBUG] Distance domicile commercial -> 1er RDV: ~15km (estimation)');
      }
      
      for (let i = 0; i < sortedRdvs.length - 1; i++) {
        const travel = calculateTravelTimeBetweenRdv(sortedRdvs[i], sortedRdvs[i + 1], commercial);
        if (travel.distance !== '?') {
          totalDistance += parseFloat(travel.distance);
        }
      }
    }
    
    console.log(`[DEBUG] Distance totale calculée: ${totalDistance.toFixed(1)}km pour ${rdvs.length} RDV`);
    return totalDistance.toFixed(1);
  };

  // Fonction pour optimiser une date spécifique pour tous les commerciaux
  const optimizeDateForAllCommercials = async (date) => {
    setIsOptimizing(true);
    
    // Mettre temporairement la date sélectionnée
    const originalDate = selectedDate;
    setSelectedDate(date);
    
    try {
      const commercialsForDate = [...new Set(
        rdvAttribues
          .filter(rdv => 
            new Date(rdv.date_rdv).toISOString().split('T')[0] === date &&
            rdv.status !== "realise" // Exclure les RDV réalisés
          )
          .map(rdv => rdv.assigned_to)
          .filter(Boolean)
      )];
      
      if (commercialsForDate.length === 0) {
        toast('Aucun commercial avec RDV cette date', { icon: 'ℹ️' });
        return;
      }

      const optimizedResults = {};
      
      for (const commercialId of commercialsForDate) {
        const result = await optimizeRouteForCommercial(commercialId);
        if (result) {
          optimizedResults[commercialId] = result;
        }
      }
      
      setOptimizedRoutes(prev => ({ ...prev, ...optimizedResults }));
      toast.success(`✨ Journée du ${new Date(date + 'T12:00:00').toLocaleDateString('fr-FR')} optimisée pour ${commercialsForDate.length} commerciaux`);
      
    } catch (error) {
      console.error('Error optimizing date:', error);
      toast.error('Erreur lors de l\'optimisation de la journée');
    } finally {
      setSelectedDate(originalDate); // Restaurer la date originale
      setIsOptimizing(false);
    }
  };

  const handleOptimizeAll = async () => {
    setIsOptimizing(true);
    try {
      // Pour l'optimisation globale, on optimise pour tous les commerciaux qui ont des RDV attribués
      // Exclure les RDV avec statut "réalisé"
      const uniqueCommercials = [...new Set(
        rdvAttribues
          .filter(rdv => rdv.status !== "realise") // Exclure les RDV réalisés
          .map(rdv => rdv.assigned_to)
          .filter(Boolean)
      )];
      
      if (uniqueCommercials.length === 0) {
        toast('Aucun commercial avec RDV attribué à optimiser', { icon: 'ℹ️' });
        return;
      }

      const optimizedResults = {};
      
      // Optimiser pour chaque commercial
      for (const commercialId of uniqueCommercials) {
        const result = await optimizeRouteForCommercial(commercialId);
        if (result) {
          optimizedResults[commercialId] = result;
        }
      }
      
      setOptimizedRoutes(optimizedResults);
      toast.success(`Optimisation globale terminée pour ${uniqueCommercials.length} commerciaux`);
    } catch (error) {
      console.error('Error in global optimization:', error);
      toast.error('Erreur lors de l\'optimisation globale');
    } finally {
      setIsOptimizing(false);
    }
  };

  // Fonction pour obtenir la couleur selon la distance du prospect
  const getProximityColor = (rdv) => {
    if (!rdv.prospect?.latitude || !rdv.prospect?.longitude) return 'bg-gray-100';
    
    // Calcul simple de distance par rapport au centre (à améliorer avec vraie distance)
    const distance = Math.sqrt(
      Math.pow(rdv.prospect.latitude - mapCenter[0], 2) + 
      Math.pow(rdv.prospect.longitude - mapCenter[1], 2)
    );
    
    if (distance < 0.01) return 'bg-green-100 border-green-300';
    if (distance < 0.05) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planifie': return 'bg-blue-100 text-blue-800';
      case 'realise': return 'bg-green-100 text-green-800';
      case 'reporte': return 'bg-yellow-100 text-yellow-800';
      case 'annule': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Chargement de la distribution...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Route className="w-6 h-6" />
            Distribution Intelligente des RDV
          </h1>
          <p className="text-gray-600 text-sm">
            Optimisation géographique et attribution aux commerciaux
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
          <Button 
            onClick={openAssignDialog}
            disabled={selectedRdv.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Users className="w-4 h-4 mr-2" />
            Attribuer ({selectedRdv.length})
          </Button>
          <Button 
            onClick={handleOptimizeAll}
            disabled={isOptimizing}
            className="bg-green-600 hover:bg-green-700"
          >
            <Zap className="w-4 h-4 mr-2" />
            {isOptimizing ? 'Optimisation...' : 'Optimiser Tout'}
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">
              {rdvNonAttribues.length}
            </div>
            <div className="text-sm text-gray-600">RDV non attribués</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {commerciaux.length}
            </div>
            <div className="text-sm text-gray-600">Commerciaux/Régies</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {selectedRdv.length}
            </div>
            <div className="text-sm text-gray-600">RDV sélectionnés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              {Object.keys(optimizedRoutes).length}
            </div>
            <div className="text-sm text-gray-600">Itinéraires optimisés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-600">
              {conversionStats.rdv_signes}
            </div>
            <div className="text-sm text-gray-600">RDV signés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-teal-600">
              {conversionStats.taux_conversion.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Taux conversion</div>
          </CardContent>
        </Card>
      </div>

      {/* Onglets RDV */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div className="flex space-x-4">
              <button
                className={`px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'non-attribues' 
                    ? 'bg-orange-100 text-orange-800 font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('non-attribues')}
              >
                RDV à Attribuer ({rdvNonAttribues.length})
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'attribues' 
                    ? 'bg-green-100 text-green-800 font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('attribues')}
              >
                RDV Attribués ({rdvAttribues.length})
              </button>
              <button
                className={`px-4 py-2 rounded-md transition-colors ${
                  activeTab === 'signes' 
                    ? 'bg-emerald-100 text-emerald-800 font-medium' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setActiveTab('signes')}
              >
                RDV Signés ({rdvSignes.length})
              </button>
            </div>
            {activeTab === 'non-attribues' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSelectAllRdv}
              >
                {selectedRdv.length === rdvNonAttribues.length ? 'Désélectionner tout' : 'Sélectionner tout'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'non-attribues' ? (
            // Contenu RDV non attribués
            rdvNonAttribues.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <div>Aucun rendez-vous non attribué</div>
              </div>
            ) : (
            <div className="space-y-2">
              {rdvNonAttribues.map((rdv) => (
                <div
                  key={rdv.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedRdv.some(r => r.id === rdv.id) 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  } ${getProximityColor(rdv)}`}
                  onClick={() => handleSelectRdv(rdv)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Informations RDV */}
                    <div className="flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={getStatusColor(rdv.status)}>
                          {rdv.status}
                        </Badge>
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-3 h-3" />
                          {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')} à{' '}
                          {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>

                      {/* Informations prospect */}
                      {rdv.prospect && (
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="font-medium">
                              {rdv.prospect.prenom} {rdv.prospect.nom}
                              {rdv.prospect.nom_entreprise && (
                                <span className="text-gray-600 ml-2">
                                  ({rdv.prospect.nom_entreprise})
                                </span>
                              )}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span className="text-sm">{rdv.prospect.telephone}</span>
                          </div>
                          
                          <div className="flex items-start gap-2">
                            <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                            <span className="text-sm break-words">
                              {rdv.prospect.adresse}
                              {(rdv.prospect.code_postal || rdv.prospect.ville) && (
                                <span className="block text-gray-500 text-xs mt-1">
                                  {rdv.prospect.code_postal && `${rdv.prospect.code_postal}`}
                                  {rdv.prospect.ville && ` ${rdv.prospect.ville}`}
                                </span>
                              )}
                            </span>
                          </div>

                          {rdv.prospect.type_energie && (
                            <div className="flex items-center gap-2">
                              <Zap className="w-3 h-3 text-gray-400" />
                              <Badge variant="outline" className="text-xs">
                                {rdv.prospect.type_energie}
                              </Badge>
                              {rdv.prospect.consommation_annuelle_euro > 0 && (
                                <span className="text-xs text-gray-600">
                                  {rdv.prospect.consommation_annuelle_euro}€/an
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Informations géographiques */}
                    <div className="text-right text-sm text-gray-500">
                      {rdv.prospect?.latitude && rdv.prospect?.longitude ? (
                        <div className="flex items-center gap-1">
                          <Navigation className="w-3 h-3" />
                          <span>Géolocalisé</span>
                        </div>
                      ) : (
                        <span className="text-orange-500">Non géolocalisé</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )
          ) : activeTab === 'attribues' ? (
            // Contenu RDV attribués
            rdvAttribues.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <div>Aucun rendez-vous attribué</div>
              </div>
            ) : (
              <div className="space-y-3">
                {rdvAttribues.map((rdv) => (
                  <div key={rdv.id} className="p-4 border rounded-lg bg-green-50 border-green-200">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Informations RDV */}
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge className={getStatusColor(rdv.status)}>
                            {rdv.status}
                          </Badge>
                          {rdv.resultat && (
                            <Badge className="bg-purple-100 text-purple-800">
                              {rdv.resultat}
                            </Badge>
                          )}
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Clock className="w-3 h-3" />
                            {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')} à{' '}
                            {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </div>
                        </div>

                        {/* Informations prospect */}
                        {rdv.prospect && (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <User className="w-3 h-3 text-gray-400" />
                              <span className="font-medium">
                                {rdv.prospect.prenom} {rdv.prospect.nom}
                                {rdv.prospect.nom_entreprise && (
                                  <span className="text-gray-600 ml-2">
                                    ({rdv.prospect.nom_entreprise})
                                  </span>
                                )}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="text-sm">{rdv.prospect.telephone}</span>
                            </div>
                            
                            <div className="flex items-start gap-2">
                              <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                              <span className="text-sm break-words">
                                {rdv.prospect.adresse}
                                {(rdv.prospect.code_postal || rdv.prospect.ville) && (
                                  <span className="block text-gray-500 text-xs mt-1">
                                    {rdv.prospect.code_postal && `${rdv.prospect.code_postal}`}
                                    {rdv.prospect.ville && ` ${rdv.prospect.ville}`}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Informations commercial */}
                      <div className="text-right">
                        <div className="text-sm">
                          <div className="font-medium text-green-700">
                            Assigné à :
                          </div>
                          <div className="text-green-600">
                            {rdv.commercial ? (
                              `${rdv.commercial.first_name} ${rdv.commercial.last_name}`
                            ) : (
                              'Commercial introuvable'
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {rdv.assigned_at ? (
                              `Le ${new Date(rdv.assigned_at).toLocaleDateString('fr-FR')}`
                            ) : 'Date d\'attribution inconnue'}
                          </div>
                        </div>
                        
                        {/* Bouton Modifier Statut */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 text-xs"
                          onClick={() => openStatusDialog(rdv)}
                        >
                          Modifier Statut
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Contenu RDV signés
            <div className="space-y-4">
              {rdvSignes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun rendez-vous signé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rdvSignes.map((rdv) => (
                    <div key={rdv.id} className="p-4 border rounded-lg bg-emerald-50 border-emerald-200">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Informations RDV */}
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-800">
                              Signé ✓
                            </Badge>
                            <Badge className={getStatusColor(rdv.status)}>
                              {rdv.status}
                            </Badge>
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Clock className="w-3 h-3" />
                              {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')} à{' '}
                              {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </div>
                          </div>

                          {/* Informations prospect */}
                          {rdv.prospect && (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-gray-400" />
                                <span className="font-medium">
                                  {rdv.prospect.prenom} {rdv.prospect.nom}
                                  {rdv.prospect.nom_entreprise && (
                                    <span className="text-gray-600 ml-2">
                                      ({rdv.prospect.nom_entreprise})
                                    </span>
                                  )}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Phone className="w-3 h-3 text-gray-400" />
                                <span className="text-sm">{rdv.prospect.telephone}</span>
                              </div>
                              
                              <div className="flex items-start gap-2">
                                <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                                <span className="text-sm break-words">
                                  {rdv.prospect.adresse}
                                  {(rdv.prospect.code_postal || rdv.prospect.ville) && (
                                    <span className="block text-gray-500 text-xs mt-1">
                                      {rdv.prospect.code_postal && `${rdv.prospect.code_postal}`}
                                      {rdv.prospect.ville && ` ${rdv.prospect.ville}`}
                                    </span>
                                  )}
                                </span>
                              </div>

                              {rdv.prospect.type_energie && (
                                <div className="flex items-center gap-2">
                                  <Zap className="w-3 h-3 text-gray-400" />
                                  <Badge variant="outline" className="text-xs">
                                    {rdv.prospect.type_energie}
                                  </Badge>
                                  {rdv.prospect.consommation_annuelle_euro > 0 && (
                                    <span className="text-xs text-gray-600">
                                      {rdv.prospect.consommation_annuelle_euro}€/an
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Informations commercial et résultat */}
                        <div className="text-right">
                          <div className="text-sm space-y-1">
                            <div className="font-medium text-emerald-700">
                              Commercial :
                            </div>
                            <div className="text-emerald-600">
                              {rdv.commercial ? (
                                `${rdv.commercial.first_name} ${rdv.commercial.last_name}`
                              ) : (
                                'Commercial introuvable'
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              Signé le {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')}
                            </div>
                            {rdv.notes && (
                              <div className="text-xs text-gray-600 mt-2 max-w-xs">
                                <strong>Notes:</strong> {rdv.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vue Optimisée Jour par Jour */}
      {rdvAttribues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Route className="w-5 h-5" />
              Planification Optimisée - Vue Journalière
            </CardTitle>
            <p className="text-sm text-gray-600">
              Affichage automatique des RDV organisés par commercial avec temps de route entre chaque rendez-vous
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {renderOptimizedDailyView()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Itinéraires optimisés */}
      {Object.keys(optimizedRoutes).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Itinéraires Optimisés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(optimizedRoutes).map(([commercialId, route]) => {
                const commercial = commerciaux.find(c => c.id === commercialId);
                return (
                  <div key={commercialId} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold">
                          {commercial?.first_name} {commercial?.last_name}
                        </h3>
                        <div className="text-sm text-gray-600">
                          {route.rdv.length} RDV • {route.date}
                          {route.fallback && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Ordre chronologique
                            </Badge>
                          )}
                        </div>
                      </div>
                      {!route.fallback && (
                        <div className="text-right text-sm">
                          <div className="text-green-600 font-medium">
                            {route.distance.toFixed(1)} km
                          </div>
                          <div className="text-gray-600">
                            {Math.round(route.time)} min
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-3">
                      {route.proposedSchedule ? (
                        // Affichage avec propositions d'horaires intelligentes
                        route.proposedSchedule.map((rdv, index) => (
                          <div key={rdv.id} className={`p-3 border rounded-lg ${
                            rdv.time_changed ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
                          }`}>
                            <div className="flex items-start gap-3">
                              <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                                {index + 1}
                              </div>
                              
                              <div className="flex-1 space-y-1">
                                <div className="flex items-center justify-between">
                                  <div className="font-medium">
                                    {rdv.prospect?.prenom} {rdv.prospect?.nom}
                                  </div>
                                  {rdv.distance_from_previous > 0 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{rdv.distance_from_previous}km ({rdv.travel_time}min)
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="text-sm text-gray-600">
                                  {rdv.prospect?.adresse}
                                  {(rdv.prospect?.code_postal || rdv.prospect?.ville) && (
                                    <span className="block text-gray-500 text-xs">
                                      {rdv.prospect?.code_postal && `${rdv.prospect.code_postal}`}
                                      {rdv.prospect?.ville && ` ${rdv.prospect.ville}`}
                                    </span>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-4 text-xs">
                                  <div className={rdv.time_changed ? 'text-orange-600' : 'text-green-600'}>
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    Actuel: {rdv.original_time.toLocaleTimeString('fr-FR', { 
                                      hour: '2-digit', 
                                      minute: '2-digit' 
                                    })}
                                  </div>
                                  
                                  {rdv.time_changed && (
                                    <div className="text-blue-600 font-medium">
                                      → Proposé: {rdv.proposed_time.toLocaleTimeString('fr-FR', { 
                                        hour: '2-digit', 
                                        minute: '2-digit' 
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        // Affichage simple pour fallback
                        route.rdv.map((rdv, index) => (
                          <div key={rdv.id} className="flex items-center gap-3 text-sm">
                            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium">
                                {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })} - {rdv.prospect?.prenom} {rdv.prospect?.nom}
                              </div>
                              <div className="text-gray-600">
                                {rdv.prospect?.adresse}
                                {(rdv.prospect?.code_postal || rdv.prospect?.ville) && (
                                  <span className="block text-gray-500 text-xs">
                                    {rdv.prospect?.code_postal && `${rdv.prospect.code_postal}`}
                                    {rdv.prospect?.ville && ` ${rdv.prospect.ville}`}
                                  </span>
                                )}
                              </div>
                            </div>
                            {index < route.rdv.length - 1 && (
                              <ArrowRight className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                        ))
                      )}
                      
                      {route.savings && (
                        <div className="mt-3 p-2 bg-green-100 rounded text-xs text-green-800">
                          💡 Optimisation: ~{route.savings.time}min économisées, {route.savings.distance}km en moins
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog d'attribution */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Attribuer {selectedRdv.length} RDV</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Commercial / Régie</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={selectedCommercial}
                onChange={(e) => setSelectedCommercial(e.target.value)}
              >
                <option value="">Sélectionner...</option>
                {commerciaux.map(commercial => (
                  <option key={commercial.id} value={commercial.id}>
                    {commercial.first_name} {commercial.last_name} ({commercial.role})
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600">
              <h4 className="font-medium mb-2">RDV sélectionnés :</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {selectedRdv.map(rdv => (
                  <div key={rdv.id} className="text-xs">
                    • {rdv.prospect?.prenom} {rdv.prospect?.nom} - {' '}
                    {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAssignRdv} className="bg-blue-600 hover:bg-blue-700">
              Attribuer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de modification de statut RDV */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le statut du RDV</DialogTitle>
          </DialogHeader>
          
          {editingRdvStatus && (
            <div className="space-y-4">
              {/* Informations RDV */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium">
                  {editingRdvStatus.prospect?.prenom} {editingRdvStatus.prospect?.nom}
                </div>
                <div className="text-sm text-gray-600">
                  {new Date(editingRdvStatus.date_rdv).toLocaleDateString('fr-FR')} à{' '}
                  {new Date(editingRdvStatus.date_rdv).toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>

              {/* Statut */}
              <div>
                <Label>Statut du RDV</Label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="planifie">Planifié</option>
                  <option value="realise">Réalisé</option>
                  <option value="reporte">Reporté</option>
                  <option value="annule">Annulé</option>
                </select>
              </div>

              {/* Résultat */}
              <div>
                <Label>Résultat du RDV</Label>
                <select
                  value={newResultat}
                  onChange={(e) => setNewResultat(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Sélectionnez un résultat --</option>
                  <option value="signe">Signé</option>
                  <option value="non_signe">Non signé</option>
                  <option value="r2">R2 (Rappel téléphonique)</option>
                  <option value="hors_critere">Hors critère</option>
                  <option value="pas_interesse">Pas intéressé</option>
                  <option value="injoignable">Injoignable</option>
                </select>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                  Annuler
                </Button>
                <Button onClick={handleUpdateRdvStatus} className="bg-green-600 hover:bg-green-700">
                  Modifier
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DistributionRdvPage;